import { ContactLink } from '@/components/contact';

/**
 * PrivacyPolicyContent Component
 *
 * Reusable privacy policy content for:
 * - Modal display during signup (scroll-to-bottom requirement)
 * - Standalone page at /privacy
 *
 * WP-CONTACT-1: Updated to use ContactLink instead of mailto
 */
export function PrivacyPolicyContent() {
  const effectiveDate = 'December 1, 2025';
  const lastUpdated = 'February 11, 2026';

  return (
    <>
      {/* Dates */}
      <div className="text-sm text-gray-500 space-y-1 mb-8">
        <p>
          <span className="font-medium">Effective Date:</span> {effectiveDate}
        </p>
        <p>
          <span className="font-medium">Last Updated:</span> {lastUpdated}
        </p>
      </div>

      {/* Table of Contents */}
      <nav className="mb-10 p-6 bg-gray-50 rounded-lg" aria-label="Table of contents">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Table of Contents
        </h2>
        <ol className="list-decimal list-inside space-y-2 text-sm text-primary-600">
          <li><a href="#introduction" className="hover:underline">Introduction</a></li>
          <li><a href="#information-we-collect" className="hover:underline">Information We Collect</a></li>
          <li><a href="#how-we-use-information" className="hover:underline">How We Use Your Information</a></li>
          <li><a href="#data-sharing" className="hover:underline">Data Sharing and Third Parties</a></li>
          <li><a href="#data-retention" className="hover:underline">Data Retention</a></li>
          <li><a href="#security" className="hover:underline">Security Measures</a></li>
          <li><a href="#your-rights" className="hover:underline">Your Rights and Choices</a></li>
          <li><a href="#cookies" className="hover:underline">Cookies and Tracking Technologies</a></li>
          <li><a href="#childrens-privacy" className="hover:underline">Children's Privacy</a></li>
          <li><a href="#international-users" className="hover:underline">International Users</a></li>
          <li><a href="#changes" className="hover:underline">Changes to This Policy</a></li>
          <li><a href="#contact" className="hover:underline">Contact Us</a></li>
        </ol>
      </nav>

      {/* Policy Sections */}
      <div className="prose prose-gray max-w-none">
        {/* Section 1: Introduction */}
        <section id="introduction" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            1. Introduction
          </h2>
          <p className="text-gray-600 mb-4">
            Welcome to FlowIO. We are committed to protecting your privacy and ensuring
            the security of your personal and financial information. This Privacy Policy
            explains how FlowIO ("we," "our," or "us") collects, uses, discloses, and
            safeguards your information when you use our personal finance management
            application and related services (collectively, the "Service").
          </p>
          <p className="text-gray-600 mb-4">
            By accessing or using our Service, you acknowledge that you have read,
            understood, and agree to be bound by this Privacy Policy. If you do not
            agree with our policies and practices, please do not use our Service.
          </p>
          <p className="text-gray-600">
            This policy applies to information we collect through our website, mobile
            applications, and any other online features, services, or platforms we
            offer that link to this Privacy Policy.
          </p>
        </section>

        {/* Section 2: Information We Collect */}
        <section id="information-we-collect" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            2. Information We Collect
          </h2>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            2.1 Information You Provide Directly
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>
              <span className="font-medium">Account Information:</span> When you create
              an account, we collect your email address, password (stored securely using
              industry-standard hashing), and optional profile information such as your
              name.
            </li>
            <li>
              <span className="font-medium">Communication Data:</span> When you contact
              our support team or respond to surveys, we collect the information you
              provide in those communications.
            </li>
            <li>
              <span className="font-medium">Payment Information:</span> When you
              subscribe to our paid services, payment processing is handled by Stripe.
              We do not store your full credit card number; we only receive limited
              information such as the last four digits and card type for your records.
            </li>
            <li>
              <span className="font-medium">Phone Number:</span> If you enable SMS-based
              multi-factor authentication, we collect your mobile phone number. Your
              phone number is encrypted at rest and is used exclusively for sending
              security verification codes.
            </li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            2.2 Financial Data from Connected Accounts
          </h3>
          <p className="text-gray-600 mb-4">
            With your explicit consent, we use Plaid Inc. to securely connect to your
            financial institutions. Through this connection, we collect:
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Account information (account names, types, balances)</li>
            <li>Transaction history (date, merchant, amount, category)</li>
            <li>Financial institution information (bank name, routing numbers)</li>
            <li>Account holder information as provided by your financial institution</li>
          </ul>
          <p className="text-gray-600 mb-4">
            We do not store your financial institution login credentials. Plaid securely
            manages the connection between FlowIO and your bank.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            2.3 Information Collected Automatically
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>
              <span className="font-medium">Device Information:</span> Browser type,
              operating system, device identifiers, and device fingerprint data used
              for fraud prevention and security.
            </li>
            <li>
              <span className="font-medium">Usage Data:</span> Pages visited, features
              used, click patterns, time spent on pages, and navigation paths.
            </li>
            <li>
              <span className="font-medium">Log Data:</span> IP address, access times,
              referring URLs, and error logs (sanitized to remove personal information).
            </li>
            <li>
              <span className="font-medium">Location Data:</span> General geographic
              location based on IP address (country/region level only).
            </li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            2.4 Documents You Upload
          </h3>
          <p className="text-gray-600 mb-4">
            You may upload financial documents such as bank statements, CSV exports, or
            PDF files to import transaction data. Uploaded documents are encrypted using
            AES-256 encryption during processing. Original documents are automatically
            deleted within 30 days after successful import.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            2.5 Authentication Credentials
          </h3>
          <p className="text-gray-600 mb-4">
            We offer multiple authentication methods for your security:
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>
              <span className="font-medium">Passkeys/WebAuthn:</span> If you register a
              passkey for passwordless login, we store a cryptographic credential ID and
              public key. <strong>No biometric data is ever transmitted to our servers.</strong>
            </li>
            <li>
              <span className="font-medium">TOTP Authenticator Apps:</span> If you enable
              authenticator-based MFA, we store an encrypted seed for generating time-based
              codes.
            </li>
            <li>
              <span className="font-medium">Magic Links:</span> Temporary secure tokens
              sent to your email that expire after 15 minutes.
            </li>
          </ul>
        </section>

        {/* Section 3: How We Use Information */}
        <section id="how-we-use-information" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            3. How We Use Your Information
          </h2>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            3.1 Service Delivery
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Provide, maintain, and improve our Service</li>
            <li>Aggregate and categorize your financial transactions</li>
            <li>Generate personalized financial visualizations and insights</li>
            <li>Process your subscription payments</li>
            <li>Respond to your inquiries and support requests</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            3.2 Communication
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Send transactional emails (account verification, password resets)</li>
            <li>Deliver service-related notifications and updates</li>
            <li>Send promotional communications (only with your consent)</li>
            <li>Send SMS verification codes for multi-factor authentication</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            3.3 Security and Compliance
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Detect, prevent, and address fraud and security threats</li>
            <li>Verify your identity and prevent unauthorized access</li>
            <li>Comply with legal obligations and regulatory requirements</li>
            <li>Enforce our Terms of Service and other policies</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            3.4 Automated Processing
          </h3>
          <p className="text-gray-600 mb-4">
            We use automated systems to process your financial data, including:
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Transaction Categorization using machine learning</li>
            <li>Document Classification for uploaded statements</li>
            <li>Transfer Detection between your accounts</li>
            <li>Fraud Detection for account security</li>
          </ul>
          <p className="text-gray-600">
            You can manually override any automatic categorization. GDPR users have the
            right to request human review of automated decisions.
          </p>
        </section>

        {/* Section 4: Data Sharing */}
        <section id="data-sharing" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            4. Data Sharing and Third Parties
          </h2>
          <p className="text-gray-600 mb-4">
            <strong>We do not sell your personal information.</strong> We share your
            information only in the following circumstances:
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            4.1 Service Providers
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li><span className="font-medium">Plaid Inc.:</span> Financial data aggregation</li>
            <li><span className="font-medium">Akoya:</span> Alternative financial data aggregation</li>
            <li><span className="font-medium">Stripe Inc.:</span> Payment processing</li>
            <li><span className="font-medium">Postmark:</span> Email delivery</li>
            <li><span className="font-medium">Twilio Inc.:</span> SMS delivery for MFA</li>
            <li><span className="font-medium">Linode (Akamai):</span> Cloud infrastructure</li>
            <li><span className="font-medium">Neon:</span> Database hosting</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            4.2 Legal Requirements
          </h3>
          <p className="text-gray-600 mb-4">
            We may disclose your information if required by law, such as in response to
            subpoenas, court orders, or government requests.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            4.3 Business Transfers
          </h3>
          <p className="text-gray-600">
            If FlowIO is involved in a merger or acquisition, your information may be
            transferred. We will notify you of any such change.
          </p>
        </section>

        {/* Section 5: Data Retention */}
        <section id="data-retention" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            5. Data Retention
          </h2>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>
              <span className="font-medium">Account Data:</span> Retained while active
              and up to 30 days after deletion request.
            </li>
            <li>
              <span className="font-medium">Financial Data:</span> Retained while active;
              permanently deleted within 30 days of account deletion.
            </li>
            <li>
              <span className="font-medium">Usage Data:</span> Aggregated, anonymized
              data may be retained indefinitely.
            </li>
          </ul>
        </section>

        {/* Section 6: Security */}
        <section id="security" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            6. Security Measures
          </h2>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            6.1 Encryption
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>In Transit: TLS 1.3 (HTTPS) for all communications</li>
            <li>At Rest: AES-256 encryption for sensitive data</li>
            <li>Passwords: Argon2 hashing with unique salts</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            6.2 Access Controls
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Multi-factor authentication (MFA) available for all accounts</li>
            <li>Role-based access controls for internal systems</li>
            <li>Regular security audits and penetration testing</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            6.3 Infrastructure Security
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>SOC 2 compliant data centers</li>
            <li>Intrusion detection and monitoring</li>
            <li>Regular automated encrypted backups</li>
          </ul>
        </section>

        {/* Section 7: Your Rights */}
        <section id="your-rights" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            7. Your Rights and Choices
          </h2>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            7.1 Access and Portability
          </h3>
          <p className="text-gray-600 mb-4">
            Request a copy of your personal information or export your data in a
            machine-readable format from your account settings.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            7.2 Correction and Deletion
          </h3>
          <p className="text-gray-600 mb-4">
            Update your information in account settings or request deletion. Deletion
            requests are processed within 30 days.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            7.3 GDPR Rights (European Users)
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Right to restrict processing</li>
            <li>Right to object to processing</li>
            <li>Right to withdraw consent</li>
            <li>Right to lodge a complaint with supervisory authority</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            7.4 CCPA Rights (California Residents)
          </h3>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li>Right to know what personal information is collected</li>
            <li>Right to know whether data is sold or disclosed</li>
            <li>Right to say no to sale (we do not sell your data)</li>
            <li>Right to equal service and price</li>
          </ul>
          <p className="text-gray-600">
            To exercise any rights,{' '}
            <ContactLink
              topic="privacy"
              context="PrivacyPolicyContent - CCPA/GDPR rights"
              className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
            >
              contact our privacy team
            </ContactLink>.
          </p>
        </section>

        {/* Section 8: Cookies */}
        <section id="cookies" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            8. Cookies and Tracking Technologies
          </h2>
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
            <li><span className="font-medium">Essential Cookies:</span> Required for Service function; cannot be disabled</li>
            <li><span className="font-medium">Functional Cookies:</span> Remember your preferences</li>
            <li><span className="font-medium">Analytics Cookies:</span> Help us understand usage; can be opted out</li>
          </ul>
          <p className="text-gray-600">
            Most browsers allow you to control cookies through settings. Visit{' '}
            <a
              href="https://www.allaboutcookies.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              www.allaboutcookies.org
            </a>{' '}
            for more information.
          </p>
        </section>

        {/* Section 9: Children's Privacy */}
        <section id="childrens-privacy" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            9. Children's Privacy
          </h2>
          <p className="text-gray-600">
            Our Service is not intended for individuals under 18. We do not knowingly
            collect information from children. If you believe a child has provided us
            with personal information,{' '}
            <ContactLink
              topic="privacy"
              context="PrivacyPolicyContent - Children's privacy concern"
              className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
            >
              contact our privacy team
            </ContactLink>.
          </p>
        </section>

        {/* Section 10: International Users */}
        <section id="international-users" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            10. International Users
          </h2>
          <p className="text-gray-600 mb-4">
            FlowIO is operated from the United States. By using our Service, you consent
            to the transfer of your information to the United States.
          </p>
          <p className="text-gray-600">
            For EEA users, we rely on Standard Contractual Clauses approved by the
            European Commission for data transfers.
          </p>
        </section>

        {/* Section 11: Changes */}
        <section id="changes" className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            11. Changes to This Policy
          </h2>
          <p className="text-gray-600 mb-4">
            We may update this Privacy Policy from time to time. When we make material
            changes, we will update the "Last Updated" date and notify you by email or
            through a prominent notice.
          </p>
        </section>

        {/* Section 12: Contact */}
        <section id="contact" className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            12. Contact Us
          </h2>
          <div className="bg-gray-50 rounded-lg p-6 mb-4">
            <p className="text-gray-700 mb-2">
              <span className="font-medium">Contact:</span>{' '}
              <ContactLink
                topic="privacy"
                context="PrivacyPolicyContent - Contact section"
                className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              >
                Privacy Team
              </ContactLink>
            </p>
            <p className="text-gray-600 text-sm">
              We will respond within 30 days.
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-6">
            <p className="text-gray-700 font-medium mb-2">Data Protection Officer (GDPR)</p>
            <p className="text-gray-700">
              <ContactLink
                topic="dpo"
                context="PrivacyPolicyContent - DPO contact"
                className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              >
                Contact DPO
              </ContactLink>
            </p>
          </div>
        </section>
      </div>

      {/* Footer Note */}
      <footer className="mt-10 pt-8 border-t border-gray-200">
        <p className="text-sm text-gray-500 text-center">
          This Privacy Policy was last updated on {lastUpdated}. By continuing to use
          FlowIO, you acknowledge that you have read and understood this policy.
        </p>
      </footer>
    </>
  );
}

export default PrivacyPolicyContent;
